import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import {
  isVideoAttachmentMime,
  MAX_ATTACHMENT_SIZE_BYTES,
  maxAttachmentSizeBytes,
  VIDEO_ATTACHMENT_MIMES,
} from '@wix/ai-scheduling-assistant-constants';


export const MAX_ATTACHMENT_SIZE_MB = Math.round(MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024));

// The doc types the assistant can read: the ones a provider takes natively, plus the Office formats the
// harness converts to text server-side (see resolve-attachment-content.ts) — xlsx/xls to CSV, docx/pptx to
// markdown. Kept in step with native `ChatAttachmentPolicy` / `AttachmentPolicy` and, above all, with the
// SERVER's own list in `mediaUploadUrlEndpointBuilder.ts`: that one is the real gate, and a mime offered
// here but missing there fails at the mint with a 415.
const ALLOWED_DOC_MIMES: readonly string[] = [
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/markdown',
  'text/html',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // A ZIP is expanded member-by-member server-side (see resolve-attachment-content.ts) — it's how a user
  // hands over a set of files that would never fit the per-file cap individually.
  'application/zip',
  'application/x-zip-compressed',
];

export function isMimeSupported(mime: string): boolean {
  if (mime.startsWith('image/')) return true;
  // Voice notes: the composer's recorder uploads audio/webm (or audio/mp4 on
  // Safari) through this same path; the harness transcribes on receipt. Checked before the video
  // containers, so `audio/mp4` resolves here rather than colliding with `video/mp4`.
  if (mime.startsWith('audio/')) return true;
  // Video rides as a URL only — no model reads it (`resolve-attachment-content.ts` short-circuits it),
  // so it is here for the agent to hand to a connector, and it carries its own much larger size cap.
  if (isVideoAttachmentMime(mime)) return true;
  return ALLOWED_DOC_MIMES.includes(mime);
}

// Accept attribute for <input type="file"> and matching dragenter checks.
export const ATTACHMENT_ACCEPT = ['image/*', ...VIDEO_ATTACHMENT_MIMES, ...ALLOWED_DOC_MIMES].join(',');

// Accept attribute for the CAMERA input (`capture`): the two kinds a camera can produce, and nothing
// else — a document type here would put "Choose File" back inside a row that promises the camera.
// Naming the three video containers rather than `video/*` is what keeps an `.avi`-producing camera app
// off a path the mint would refuse; it is also what tells the browser this input takes video at all,
// which is what puts the PHOTO/VIDEO switch in the camera it opens.
export const CAMERA_CAPTURE_ACCEPT = ['image/*', ...VIDEO_ATTACHMENT_MIMES].join(',');

// Mirrors mobile services/wix-media: rewrites the WixMP thumbnail URL into a
// webp variant so the harness fetches a reasonable-sized image. This is what
// lands on the attachment's `thumbnailUrl`; the file's own url rides `url`.
function toWebpVariantUrl(thumbnailUrl: string): string {
  const base = thumbnailUrl.split('?')[0];
  return `${base}/v1/fit/w_1024,h_1024,q_85/image.webp`;
}

export type UploadFailureKind = 'unsupported' | 'too_large' | 'failed';

export type UploadResult =
  | { ok: true; attachment: MessageAttachment }
  | { ok: false; kind: UploadFailureKind; message: string };

export async function uploadAttachment(file: File): Promise<UploadResult> {
  if (!isMimeSupported(file.type)) {
    return { ok: false, kind: 'unsupported', message: 'Type not supported' };
  }

  // #7157: a full-resolution phone photo routinely exceeds the upload cap even at high JPEG quality,
  // and once picked there's no retry that can ever succeed — downscale/compress before the size check
  // and before it's ever handed to the uploader. Non-image files are untouched.
  const uploadFile = file.type.startsWith('image/') ? await downscaledImageFile(file) : file;

  // Per-kind: a video's ceiling is its own, and much larger — nothing sends it to a model.
  const sizeLimit = maxAttachmentSizeBytes(uploadFile.type);
  if (uploadFile.size > sizeLimit) {
    return { ok: false, kind: 'too_large', message: `Too large (max ${Math.round(sizeLimit / (1024 * 1024))} MB)` };
  }

  let uploadUrl: string;
  // The name AND type WixMP was told about at mint time. It types a file by the extension and
  // validates the declared type against it, so the server may repair BOTH together (`.md`/`text/markdown`
  // → `.md.txt`/`text/plain`); the PUT must match what was minted or WixMP refuses the pair. The
  // attachment we persist keeps the user's original name and type either way.
  let uploadFileName = uploadFile.name;
  let uploadMimeType = uploadFile.type;
  try {
    const res = await apiFetch(`${API_BASE}/api/upload-url`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName: uploadFile.name, mimeType: uploadFile.type, size: uploadFile.size }),
    });
    if (!res.ok) {
      return { ok: false, kind: 'failed', message: await readErrorMessage(res, 'Upload') };
    }
    const data = (await res.json()) as { uploadUrl?: string; uploadFileName?: string; uploadMimeType?: string };
    if (!data.uploadUrl) return { ok: false, kind: 'failed', message: 'Upload URL missing in response' };
    uploadUrl = data.uploadUrl;
    if (data.uploadFileName) uploadFileName = data.uploadFileName;
    if (data.uploadMimeType) uploadMimeType = data.uploadMimeType;
  } catch (err) {
    return { ok: false, kind: 'failed', message: err instanceof Error ? err.message : String(err) };
  }

  // Direct browser → WixMP PUT. Mirrors mobile's wixMediaService.uploadFile: a
  // `?filename=` query param plus the file body as raw bytes (the browser sets
  // Content-Length and reads the Blob/File natively).
  let payload: { file?: { thumbnailUrl?: string; url?: string; downloadUrl?: string } };
  try {
    const putRes = await fetch(`${uploadUrl}?filename=${encodeURIComponent(uploadFileName)}`, {
      method: 'PUT',
      headers: { 'Content-Type': uploadMimeType },
      body: uploadFile,
    });
    if (!putRes.ok) {
      return { ok: false, kind: 'failed', message: `Upload PUT failed (${putRes.status})` };
    }
    payload = await putRes.json();
  } catch (err) {
    return { ok: false, kind: 'failed', message: err instanceof Error ? err.message : String(err) };
  }

  if (file.type.startsWith('image/')) {
    const thumbnailUrl = payload?.file?.thumbnailUrl;
    // #7157: an image descriptor without a thumbnailUrl (WixMP hasn't generated one yet, or never does
    // for this file) used to fail the whole upload outright, even though the raw file url is right
    // there — the same fallback the non-image branch already had. Fall back to it instead.
    const fileUrl = payload?.file?.url ?? payload?.file?.downloadUrl;
    if (thumbnailUrl) {
      // mimeType follows `url` — describing the file, not the rendition.
      return {
        ok: true,
        attachment: {
          fileName: uploadFile.name,
          mimeType: fileUrl ? uploadFile.type : 'image/webp',
          url: fileUrl ?? toWebpVariantUrl(thumbnailUrl),
          thumbnailUrl: toWebpVariantUrl(thumbnailUrl),
          size: uploadFile.size,
        },
      };
    }
    if (!fileUrl) return { ok: false, kind: 'failed', message: 'WixMP response missing thumbnail/file URL' };
    return {
      ok: true,
      attachment: { fileName: uploadFile.name, mimeType: uploadFile.type, url: fileUrl, thumbnailUrl: fileUrl, size: uploadFile.size },
    };
  }

  const fileUrl = payload?.file?.url ?? payload?.file?.downloadUrl;
  if (!fileUrl) return { ok: false, kind: 'failed', message: 'WixMP response missing file URL' };
  return {
    ok: true,
    attachment: {
      fileName: file.name,
      mimeType: file.type,
      url: fileUrl,
      thumbnailUrl: fileUrl,
      size: uploadFile.size,
    },
  };
}

// The longest side a picked photo is downscaled to before JPEG-encoding (#7157) — a modern phone
// photo's raw pixel dimensions (commonly 4000px+ on the long side) are the main reason it lands over
// MAX_ATTACHMENT_SIZE_BYTES even at reduced JPEG quality. Mirrors native `ChatAttachmentPolicy`.
const MAX_IMAGE_DIMENSION = 2048;
// JPEG quality steps tried, in order, until the encoded size fits under the cap. The last step is
// accepted even if still over the cap — the server-side mint enforces the real limit.
const JPEG_QUALITY_STEPS = [0.7, 0.5, 0.3];

/**
 * Downscale + JPEG-compress an image File client-side so a normal phone photo fits under the upload
 * cap. Returns the original `file` unchanged if the browser can't decode it as an image (never worse
 * than the pre-fix behavior) — the existing size check still applies to whatever this returns.
 */
async function downscaledImageFile(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const longestSide = Math.max(bitmap.width, bitmap.height);
    const scale = longestSide > MAX_IMAGE_DIMENSION ? MAX_IMAGE_DIMENSION / longestSide : 1;
    const targetWidth = Math.max(1, Math.round(bitmap.width * scale));
    const targetHeight = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    bitmap.close?.();

    let best: Blob | null = null;
    for (const quality of JPEG_QUALITY_STEPS) {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (!blob) continue;
      best = blob;
      if (blob.size <= MAX_ATTACHMENT_SIZE_BYTES) break;
    }
    if (!best) return file;
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([best], name, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

async function readErrorMessage(res: Response, label: string): Promise<string> {
  const fallback = `${label} failed (${res.status})`;
  const text = await res.text().catch(() => '');
  if (!text) return fallback;
  if (text.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(text) as { message?: string; error?: string };
      const msg = parsed.message || parsed.error;
      if (msg) return msg;
    } catch {
      // fall through
    }
  }
  return fallback;
}
